import { dictionary } from '../api/dictionary';

let errorCache = null;
const LOCAL_MESSAGES = {
  'GUEST.PROJECT_LIMIT_EXCEEDED': 'Guest mode allows one project. Reopen your existing project or sign in to create more.',
  'GUEST.DOWNLOAD_LIMIT_EXCEEDED': 'Guest download limit reached. Sign in to unlock more downloads.',
  'USAGE.QUOTA_EXCEEDED': 'You have used all of this on your current plan. Archive something you no longer need, or upgrade to add more.',
  // HTTP status-level fallbacks
  'SYSTEM.SERVICE_UNAVAILABLE': 'Service temporarily unavailable. Please try again in a moment.',
  'SYSTEM.CIRCUIT_BREAKER_OPEN': 'Service temporarily unavailable. Please try again in a moment.',
  'SYSTEM.TIMEOUT': 'The request timed out. Please try again.',
  'SYSTEM.INTERNAL_SERVER_ERROR': 'Something went wrong on our end. Please try again.',
  'SYSTEM.GATEWAY_TIMEOUT': 'The server took too long to respond. Please try again.',

  // 🔑 PASSWORD RESET REFUSALS.
  //
  // ⚠️ These all fell through to the generic 403 copy — "You do not have
  // permission to perform this action" — which is not what went wrong in any
  // of them, and leaves the person with nothing to do about it. The reset link
  // is valid; something about the ACCOUNT is the problem, and only naming it
  // gives them a next step.
  'AUTH.PASSWORD_RESET_UNVERIFIED_ACCOUNT':
    'Your email address hasn\'t been verified yet, so we can\'t reset the password on this account. Verify your email first, then try again.',
  'AUTH.PASSWORD_RESET_ACCOUNT_SUSPENDED':
    'This account is suspended, so its password can\'t be reset. Please contact support.',
  'AUTH.PASSWORD_RESET_ACCOUNT_BANNED':
    'This account has been closed. Please contact support if you think that\'s wrong.',
  'AUTH.PASSWORD_RESET_INVALID_TOKEN':
    'That reset link has expired or has already been used. Request a new one.',
  'AUTH.PASSWORD_RESET_USER_NOT_FOUND':
    'That reset link is no longer valid. Request a new one.',
};

const normalizeCode = (code) => {
  if (!code || typeof code !== 'string') return 'SYSTEM.INTERNAL_SERVER_ERROR';
  return code.startsWith('V1.') ? code.slice(3) : code;
};

/**
 * The API error code, with the transport's version prefix removed.
 *
 * The gateway stamps EVERY error it returns: `code.startsWith('V1.') ? code :
 * \`V1.\${code}\``. So a service raising GUEST.PROJECT_LIMIT_EXCEEDED reaches
 * the browser as V1.GUEST.PROJECT_LIMIT_EXCEEDED, and a v2 service's
 * V2.ADMIN_ONLY arrives as V1.V2.ADMIN_ONLY.
 *
 * Callers were comparing the raw code against the unprefixed constant, so the
 * branch never ran. On the home page that made the primary call to action look
 * dead: a guest at their project limit got no navigation, no toast, no
 * explanation — the recovery branch existed and could not be reached.
 *
 * This existed already as a private helper inside this file, doing exactly
 * this job for message lookup. It was not exported, so every caller outside
 * re-implemented the comparison and got it wrong. Exported now: one definition
 * of what an error code is.
 *
 * Returns '' for a missing code rather than the INTERNAL_SERVER_ERROR fallback
 * above, because a comparison wants "no code" to match nothing, where a
 * message lookup wants something to show.
 */
export const apiErrorCode = (errorOrCode) => {
  const raw = typeof errorOrCode === 'string'
    ? errorOrCode
    : (errorOrCode?.response?.data?.code
      ?? errorOrCode?.response?.data?.error?.code
      ?? errorOrCode?.code
      ?? '');
  if (!raw || typeof raw !== 'string') return '';
  return raw.startsWith('V1.') ? raw.slice(3) : raw;
};

/**
 * 🔍 Resolver: Translates machine code to human language
 */
const resolveError = async (code) => {
  const normalizedCode = normalizeCode(code);
  if (LOCAL_MESSAGES[normalizedCode]) return LOCAL_MESSAGES[normalizedCode];

  if (!errorCache) {
    try {
      errorCache = await dictionary.getPublic('i18n/en/errors');
      if (!errorCache) return LOCAL_MESSAGES[normalizedCode] || 'Something went wrong. Please try again.';
    } catch {
      return 'Unable to connect. Please check your connection and try again.';
    }
  }
  return normalizedCode.split('.').reduce((obj, key) => obj?.[key], errorCache) || 'Something went wrong. Please try again.';
};

// Axios sets `error.message` to "Request failed with status code 400" — a
// sentence that names the number the user can already see and explains
// nothing. It must never win over the server's own account of what was wrong.
const isAxiosBoilerplate = (m) => /^Request failed with status code \d+$/i.test(m);

export const resolveApiErrorMessage = async (errorOrCode) => {
  if (errorOrCode && typeof errorOrCode === 'object') {
    const issues = Array.isArray(errorOrCode?.issues) ? errorOrCode.issues : [];
    const firstIssueMessage = String(issues[0]?.message || '').trim();
    if (firstIssueMessage) return firstIssueMessage;
  }
  // A code we have written a sentence for wins over the server's own title.
  // The server's message names the rule for an engineer ("Quota exceeded");
  // the sentence here tells the person what happened and what to do. Seen
  // live: a member at their plan's project limit got a toast reading "Quota
  // exceeded" and a modal that stayed open, with the written sentence sitting
  // unused below the 4xx branch.
  {
    // Only a code the error actually carried: an absent code normalises to
    // the generic server-error key, which must not swallow a real 4xx message.
    const carried = apiErrorCode(errorOrCode);
    if (carried && LOCAL_MESSAGES[carried]) return LOCAL_MESSAGES[carried];
  }

  // The server's message comes BEFORE the client-side one. It used to come
  // after, and axios's boilerplate satisfied the earlier branch every time —
  // so this whole block was unreachable through an HTTP error, which is the
  // only kind it was written for.
  if (errorOrCode && typeof errorOrCode === 'object') {
    const status = Number(errorOrCode?.response?.status || 0);
    // TWO envelope shapes reach this. The older one puts the message at the
    // top level; the shared @moksy/common error handler nests it under
    // `error`. Reading only the flat one meant every 4xx from a service on the
    // new envelope — which is all twenty-four of them — lost its message and
    // fell through to a generic code lookup. The gateway had the identical
    // bug in attachUpstreamError.
    const serverMessage = String(
      errorOrCode?.response?.data?.message
      ?? errorOrCode?.response?.data?.error?.message
      ?? ''
    ).trim();

    // For user-correctable client errors, prefer the concrete backend message
    // over a generic code lookup so auth/validation failures are actionable —
    // unless the "message" is a transport library's own diagnostic. The
    // gateway used to forward axios's "Request failed with status code 401"
    // in the message field of an envelope whose code said
    // AUTHENTICATION.INVALID_CREDENTIALS, and this branch printed it verbatim
    // on the login form. Fixed at the gateway too; guarded here because this
    // is the last place before a person reads it.
    if (status >= 400 && status < 500 && serverMessage && !isAxiosBoilerplate(serverMessage)) {
      return serverMessage;
    }
  }

  if (errorOrCode && typeof errorOrCode === 'object') {
    const directMessage = String(errorOrCode?.message || '').trim();
    if (directMessage && directMessage !== 'Network Error' && !isAxiosBoilerplate(directMessage)) {
      return directMessage;
    }
  }

  const code = typeof errorOrCode === 'string'
    ? normalizeCode(errorOrCode)
    : normalizeCode(errorOrCode?.response?.data?.code || errorOrCode?.code);
  return resolveError(code);
};

/**
 * 📡 Handler: Orchestrates the UI Toast response
 */
export const handleApiError = async (error, showToast) => {
  const traceId = error?.response?.data?.traceId || 'no-trace';

  // The ERROR, not the code extracted from it. This passed only the code, so
  // the resolver's "prefer the server's concrete 4xx message" branch could
  // never run through this function — every validation failure surfaced as a
  // generic lookup, which is why a 4,000-character idea rejected for length
  // reported nothing a person could act on.
  const message = await resolveApiErrorMessage(error);

  showToast({
    type: 'error',
    message,
    traceId
  });
};
